import React, {useState} from 'react';
import styled from 'styled-components';
import {graphql, useFragment} from 'react-relay';
import {FEATURE_FLAGS} from '../../../constants';
import {hasFeatureFlag} from '../../../forecast-app/shared/util/FeatureUtil';
import {CommitMutation} from '../../../mutations/CommitMutation';
import {BaselineVersionCompare} from './BaselineVersionCompare';
import {BaselineVersionTabs} from './BaselineVersionTabs';
import {CreateVersionPanel} from './CreateVersionPanel';
import {showDeleteBaselineVersionModal} from './DeleteBaselineVersionModal';
import DeleteBaselineVersionMutation from './DeleteBaselineVersionMutation';
import type {DeleteBaselineVersionMutation$data} from './__generated__/DeleteBaselineVersionMutation.graphql';
import RenameBaselineVersionMutation from './RenameBaselineVersionMutation';
import {TemplateOption} from './TemplateChoice';
import {useBaselineCompareOpen, useSelectedBaselineVersion} from './useSelectedBaselineVersion';
import type {
	BaselineVersionsSection_project$data,
	BaselineVersionsSection_project$key,
} from './__generated__/BaselineVersionsSection_project.graphql';

const BASELINE_VERSIONS_FRAGMENT = graphql`
	fragment BaselineVersionsSection_project on ProjectType
	@argumentDefinitions(versionsEnabled: {type: "Boolean!", defaultValue: false}) {
		id
		baselineTarget
		budget
		budgetType
		useFixedPriceForBaselineRevenue
		projectStartYear
		projectStartMonth
		projectStartDay
		projectEndYear
		projectEndMonth
		projectEndDay
		baselineVersions {
			id
			name
			versionNumber
			isActive
			isStandard
			summary {
				minutes
				valueOfService
				revenue
				cost
				profit
				margin
				expenseRevenue
				expenseCost
			}
		}
		baselineTemplatesForInsert @include(if: $versionsEnabled) {
			id
			name
			phaseCount
			totalMinutes
		}
	}
`;

export type BaselineVersion = NonNullable<BaselineVersionsSection_project$data['baselineVersions']>[number];

/**
 * The project's Baseline versions and which one is shown. `enabled` is false with the feature flag
 * off or before the project has versions, and the page then renders as it does without versions.
 * `baselineVersionId` is the selected version's id for the version-aware reads, writes and links, and null whenever
 * Standard is shown, as only Standard is the live Baseline. Every other version, the active one included, is
 * addressed by its own id.
 */
export const useBaselineVersions = (projectRef: BaselineVersionsSection_project$key) => {
	const project = useFragment(BASELINE_VERSIONS_FRAGMENT, projectRef);
	const versions = (project.baselineVersions ?? []).filter(Boolean);
	const {selectedId, select, selectActive} = useSelectedBaselineVersion(
		versions.map(v => ({id: v.id, isActive: v.isActive, isStandard: v.isStandard}))
	);
	const enabled = hasFeatureFlag(FEATURE_FLAGS.BASELINE_VERSIONS) && versions.length > 0;
	const selected = enabled ? versions.find(v => v.id === selectedId) ?? null : null;
	return {
		enabled,
		project,
		versions,
		selected,
		standard: versions.find(v => v.isStandard) ?? null,
		select,
		selectActive,
		baselineVersionId: selected && !selected.isStandard ? selected.id : null,
	};
};

export type BaselineVersionsState = ReturnType<typeof useBaselineVersions>;

type ProjectPeriod = Pick<
	BaselineVersionsSection_project$data,
	'projectStartYear' | 'projectStartMonth' | 'projectStartDay' | 'projectEndYear' | 'projectEndMonth' | 'projectEndDay'
>;

/** The project period as a short date range in the viewer's locale, e.g. "Jul 1 – Aug 31, 2026". */
export const formatProjectPeriod = (locale: string, period: ProjectPeriod): string | null => {
	const {projectStartYear, projectStartMonth, projectStartDay, projectEndYear, projectEndMonth, projectEndDay} = period;
	if (!projectStartYear || !projectStartMonth || !projectStartDay || !projectEndYear || !projectEndMonth || !projectEndDay) {
		return null;
	}
	const start = new Date(projectStartYear, projectStartMonth - 1, projectStartDay);
	const end = new Date(projectEndYear, projectEndMonth - 1, projectEndDay);
	// formatRange is missing from this TypeScript version's Intl types, and from older browsers.
	const formatter: Intl.DateTimeFormat & {formatRange?: (from: Date, to: Date) => string} = new Intl.DateTimeFormat(locale, {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
	});
	return typeof formatter.formatRange === 'function'
		? formatter.formatRange(start, end)
		: `${formatter.format(start)} – ${formatter.format(end)}`;
};

const Section = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	margin: 0 24px 16px;
`;

export interface BaselineVersionsSectionProps {
	versions: BaselineVersionsState;
	locked: boolean;
	quotesEnabled: boolean;
	currencySymbol: string;
}

/**
 * The version tabs with the comparison table and the create panel. + New version opens the table with the panel beside
 * it; with only Standard there is nothing to compare, so the panel shows on its own and the table appears once a
 * second version exists. The table stays open after Create or Cancel, and while versions are selected (see
 * useBaselineCompareOpen), and closes with its X, the Compare toggle, or a delete that leaves only Standard.
 */
export const BaselineVersionsSection = ({versions, locked, quotesEnabled, currencySymbol}: BaselineVersionsSectionProps) => {
	const [panelOpen, setPanelOpen] = useState(false);
	const [compareOpen, setCompareOpen] = useBaselineCompareOpen();
	const {project, select, selectActive, standard} = versions;
	const templates = (project.baselineTemplatesForInsert ?? []).filter((t): t is TemplateOption => t != null);
	// Copy and Copy scaled preview from Standard's totals, so the panel needs them to open.
	const showPanel = panelOpen && standard?.summary != null;
	const showCompare = compareOpen && versions.versions.length >= 2;
	// Changes on every create, rename and delete, so the table refetches its figures.
	const refreshKey = versions.versions.map(v => `${v.id}:${v.name}`).join('|');
	const selectedId = versions.selected?.id ?? null;

	// Selecting puts the new version in the URL, which re-runs the page query with its id; that
	// response brings the new tab, its banner and its rows, so no separate reload is needed.
	const onCreated = (id: string) => {
		setPanelOpen(false);
		select(id);
	};

	const onNewVersion = () => {
		setCompareOpen(true);
		setPanelOpen(true);
	};

	// Errors (an empty, too long or protected name) come back through the default mutation error flow.
	const onRename = (id: string, name: string) => {
		const trimmed = name.trim();
		const current = versions.versions.find(v => v.id === id)?.name;
		if (!trimmed || trimmed === current) {
			return;
		}
		CommitMutation(
			RenameBaselineVersionMutation,
			{projectId: project.id, baselineVersionId: id, name: trimmed},
			undefined,
			false
		);
	};

	const onDelete = (id: string, name: string) => {
		showDeleteBaselineVersionModal({
			versionName: name,
			onConfirm: () =>
				CommitMutation(
					DeleteBaselineVersionMutation,
					{projectId: project.id, baselineVersionId: id},
					(response: DeleteBaselineVersionMutation$data | undefined) => {
						// The URL may still name the deleted version; go to the active one, which the delete may have
						// changed, so the page reloads it. The response's list says which one is active now.
						if (selectedId === id) {
							selectActive(response?.deleteBaselineVersion?.project?.baselineVersions ?? undefined);
						}
					},
					false
				),
		});
	};

	const panel =
		showPanel && standard?.summary ? (
			<CreateVersionPanel
				projectId={project.id}
				standard={standard.summary}
				templates={templates}
				currencySymbol={currencySymbol}
				onCreated={onCreated}
				onCancel={() => setPanelOpen(false)}
			/>
		) : null;

	return (
		<Section>
			<BaselineVersionTabs
				versions={versions.versions}
				selectedId={selectedId}
				locked={locked}
				quotesEnabled={quotesEnabled}
				panelOpen={showPanel}
				compareOpen={showCompare}
				onSelect={select}
				onNewVersion={onNewVersion}
				onToggleCompare={() => setCompareOpen(!compareOpen)}
			/>
			{showCompare ? (
				<BaselineVersionCompare
					projectId={project.id}
					refreshKey={refreshKey}
					selectedId={selectedId}
					currencySymbol={currencySymbol}
					adding={panel}
					locked={locked}
					quotesEnabled={quotesEnabled}
					onSelect={select}
					onRename={onRename}
					onDelete={onDelete}
					onClose={() => setCompareOpen(false)}
				/>
			) : (
				panel
			)}
		</Section>
	);
};

export default BaselineVersionsSection;
